import React from "react";
import { Check, X, Eye, ArrowUpDown } from "lucide-react";

const statusConfig = {
  APPROVED:     "bg-green-100 text-green-700 border-green-200",
  REJECTED:     "bg-red-100 text-red-700 border-red-200",
  SUBMITTED:    "bg-blue-100 text-blue-700 border-blue-200",
  UNDER_REVIEW: "bg-yellow-100 text-yellow-700 border-yellow-200",
  DRAFT:        "bg-gray-100 text-gray-600 border-gray-200",
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${statusConfig[status] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
    {status?.replace("_", " ") || "N/A"}
  </span>
);

const SortHeader = ({ label, field, onSort }) => (
  <th
    onClick={() => onSort(field)}
    className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide cursor-pointer hover:text-gray-600 transition-colors duration-150 whitespace-nowrap select-none"
  >
    <span className="flex items-center gap-1">
      {label} <ArrowUpDown size={11} className="opacity-50" />
    </span>
  </th>
);

const AdminTable = ({ applications, onApprove, onReject, onSort, onView }) => {
  if (!applications.length)
    return (
      <div className="text-center py-14 text-gray-400 text-sm">
        No applications found matching your filters.
      </div>
    );

  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-white border-b border-gray-100 z-10">
          <tr>
            <SortHeader label="Student"     field="fullName" onSort={onSort} />
            <SortHeader label="Ward"        field="ward"     onSort={onSort} />
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">Institution</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">Course</th>
            <SortHeader label="Status"      field="status"   onSort={onSort} />
            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wide">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {applications.map((app) => {
            const canAct = app.status === "SUBMITTED";
            return (
              <tr key={app._id} className="hover:bg-gray-50 transition-colors duration-100 group">

                {/* Student */}
                <td className="px-4 py-3.5">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {app.studentId?.fullName?.charAt(0)?.toUpperCase() || "S"}
                    </div>
                    <span className="font-medium text-gray-800">{app.studentId?.fullName || "N/A"}</span>
                  </div>
                </td>

                {/* Ward */}
                <td className="px-4 py-3.5 text-gray-500 text-xs">
                  {app.studentId?.ward?.name || "N/A"}
                </td>

                {/* Institution */}
                <td className="px-4 py-3.5 text-gray-600 text-xs max-w-[140px] truncate">
                  {app.institution?.institutionName || "N/A"}
                </td>

                {/* Course */}
                <td className="px-4 py-3.5 text-gray-500 text-xs max-w-[120px] truncate">
                  {app.institution?.courseOrClass || "N/A"}
                </td>

                {/* Status */}
                <td className="px-4 py-3.5">
                  <StatusBadge status={app.status} />
                </td>

                {/* Actions */}
                <td className="px-4 py-3.5">
                  <div className="flex items-center justify-end gap-1.5">
                    {/* View */}
                    {onView && (
                      <button
                        onClick={() => onView(app)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors duration-150"
                        title="View details"
                      >
                        <Eye size={13} />
                      </button>
                    )}

                    {/* Approve */}
                    <button
                      disabled={!canAct}
                      onClick={() => onApprove(app._id)}
                      title={canAct ? "Approve" : "Cannot approve"}
                      className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-white text-xs font-medium transition-colors duration-150 ${
                        canAct
                          ? "bg-green-600 hover:bg-green-700 active:scale-95"
                          : "bg-gray-200 text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      <Check size={12} /> Approve
                    </button>

                    {/* Reject */}
                    <button
                      disabled={!canAct}
                      onClick={() => onReject(app._id)}
                      title={canAct ? "Reject" : "Cannot reject"}
                      className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-white text-xs font-medium transition-colors duration-150 ${
                        canAct
                          ? "bg-red-600 hover:bg-red-700 active:scale-95"
                          : "bg-gray-200 text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      <X size={12} /> Reject
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AdminTable;
